import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { IntroNote } from "@/components/IntroNote";
import { MappingSummary } from "@/components/connectors/MappingAudit";
import {
  Plug, Plus, Trash2, RefreshCw, CheckCircle2, AlertTriangle, Loader2,
  ExternalLink, Database, Clock, Zap, Settings2,
} from "lucide-react";

const STATUS_BADGE = {
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  beta: "bg-amber-100 text-amber-800 border-amber-200",
  simulated: "bg-violet-100 text-violet-800 border-violet-200",
  coming_soon: "bg-slate-100 text-slate-600 border-slate-200",
};

const STATUS_LABEL = {
  active: "Live",
  beta: "Beta",
  simulated: "Flip-to-live ready",
  coming_soon: "Coming soon",
};

export default function Connectors() {
  const [catalogue, setCatalogue] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configureSlug, setConfigureSlug] = useState(null);
  const [plansForId, setPlansForId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/connectors/catalogue").then((r) => r.data.items || []),
      api.get("/connectors").then((r) => r.data.items || []),
    ])
      .then(([cat, list]) => { setCatalogue(cat); setItems(list); })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load connectors"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Navbar />
      <Seo title="PPM Connectors" description="Connect ERIO to live PPM and ITSM systems." path="/connectors" />
      <main className="mx-auto max-w-5xl px-6 py-8" data-testid="connectors-page">
        <p className="ek-eyebrow">Platform · Live data</p>
        <h1 className="ek-heading mt-1 text-3xl">PPM connectors</h1>
        <IntroNote className="mt-4">
          Read-only connectors pull live plan data from your PPM / ITSM systems so ERIO can score it against the
          Defensible AI Charter. ERIO never writes back — every imported plan keeps a link to the upstream record
          so you can verify the source in one click.
        </IntroNote>

        <section className="mt-6">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-ek-ink/65">Available connectors</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2" data-testid="connectors-catalogue">
            {catalogue.map((c) => (
              <div key={c.slug} className="rounded-2xl border border-black/5 bg-white p-4"
                data-testid={`connector-cat-${c.slug}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Plug size={16} className="text-ek-purple" />
                    <h3 className="font-display text-sm font-semibold text-ek-ink">{c.name}</h3>
                  </div>
                  <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${STATUS_BADGE[c.status] || STATUS_BADGE.coming_soon}`}>
                    {STATUS_LABEL[c.status] || c.status}
                  </span>
                </div>
                <p className="mt-2 text-xs text-ek-ink/65">
                  {c.kind === "itsm" ? "IT service management" : "Project portfolio management"} · {c.credential_fields.length} credential field{c.credential_fields.length === 1 ? "" : "s"}
                </p>
                <button
                  disabled={c.status === "coming_soon"}
                  onClick={() => setConfigureSlug(c.slug)}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-black/10 bg-white px-3 py-1.5 text-xs font-medium text-ek-ink hover:bg-ek-purple/5 disabled:opacity-50"
                  data-testid={`connector-configure-${c.slug}`}>
                  <Plus size={12} /> {c.status === "coming_soon" ? "Awaiting build" : c.status === "simulated" ? "Configure (simulator)" : "Configure"}
                </button>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-8">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-ek-ink/65">Configured for this tenant</h2>
          {loading ? (
            <p className="mt-2 text-sm text-ek-ink/65">Loading…</p>
          ) : items.length === 0 ? (
            <div className="mt-2 rounded-xl border border-dashed border-black/10 bg-white p-6 text-center"
              data-testid="connectors-empty">
              <Database className="mx-auto text-ek-ink/30" size={24} />
              <p className="mt-2 text-sm text-ek-ink/65">No connectors configured yet.</p>
              <p className="mt-1 text-xs text-ek-ink/50">Pick one above to get started.</p>
            </div>
          ) : (
            <div className="mt-2 space-y-2" data-testid="connectors-list">
              {items.map((c) => (
                <ConnectorRow key={c.connector_id} c={c}
                  def={catalogue.find((d) => d.slug === c.slug)}
                  onChanged={load}
                  onShowPlans={() => setPlansForId(c.connector_id)} />
              ))}
            </div>
          )}
        </section>

        {configureSlug && (
          <ConfigureDialog
            slug={configureSlug}
            catalogue={catalogue}
            onClose={() => setConfigureSlug(null)}
            onCreated={() => { setConfigureSlug(null); load(); }}
          />
        )}

        {plansForId && (
          <PlansDialog
            connectorId={plansForId}
            onClose={() => setPlansForId(null)}
          />
        )}
      </main>
    </div>
  );
}

function ConnectorRow({ c, def, onChanged, onShowPlans }) {
  const [busy, setBusy] = useState(null);
  const [editing, setEditing] = useState(false);

  const action = async (label, fn) => {
    setBusy(label);
    try {
      await fn();
    } finally {
      setBusy(null);
    }
  };

  const remove = () => action("delete", async () => {
    if (!window.confirm(`Remove the ${c.name} connector? Synced plans for this tenant will be deleted.`)) return;
    try {
      await api.delete(`/connectors/${c.connector_id}`);
      toast.success(`${c.name} disconnected`);
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  });

  const test = () => action("test", async () => {
    try {
      const { data } = await api.post(`/connectors/${c.connector_id}/test`);
      if (data.ok) toast.success(`Test OK — ${data.detail}`);
      else toast.error(`Test failed — ${data.detail}`);
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Test failed");
    }
  });

  const sync = (full = false) => action("sync", async () => {
    try {
      const { data } = await api.post(`/connectors/${c.connector_id}/sync`, { full });
      toast.success(`${data.mode === "full" ? "Full" : "Delta"} sync — ${data.synced} plan${data.synced === 1 ? "" : "s"} updated, ${data.total} on disk`);
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Sync failed");
    }
  });

  const ok = c.last_test_ok !== false;
  return (
    <div className="rounded-xl border border-black/5 bg-white p-4"
         data-testid={`connector-row-${c.connector_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Plug size={14} className="text-ek-purple" />
            <h3 className="font-display text-sm font-semibold text-ek-ink">{c.name}</h3>
            {ok ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">
                <CheckCircle2 size={10} /> Healthy
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                <AlertTriangle size={10} /> Needs attention
              </span>
            )}
          </div>
          <p className="mt-1 text-[11px] text-ek-ink/65">
            <Clock className="-mt-0.5 mr-1 inline" size={10} />
            Last sync: {c.last_sync_at ? new Date(c.last_sync_at).toLocaleString() : "never"}
            {typeof c.last_sync_count === "number" && c.last_sync_count > 0 && ` · ${c.last_sync_count} plan${c.last_sync_count === 1 ? "" : "s"}`}
          </p>
          {c.last_error && (
            <p className="mt-1 max-w-md text-[11px] text-amber-700" data-testid={`connector-error-${c.connector_id}`}>
              {c.last_error}
            </p>
          )}
          <MappingSummary c={c} />
          <AutoSyncControl c={c} onChanged={onChanged} />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={test} disabled={busy !== null}
            className="inline-flex items-center gap-1 rounded-md border border-black/10 bg-white px-2.5 py-1 text-xs font-medium text-ek-ink hover:bg-ek-purple/5 disabled:opacity-60"
            data-testid={`connector-test-${c.connector_id}`}>
            {busy === "test" ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />} Test
          </button>
          <button onClick={() => sync(false)} disabled={busy !== null}
            className="inline-flex items-center gap-1 rounded-md bg-ek-purple px-2.5 py-1 text-xs font-medium text-white hover:bg-ek-purple/90 disabled:opacity-60"
            data-testid={`connector-sync-${c.connector_id}`}
            title="Delta sync — fetch only records updated since the last sync">
            {busy === "sync" ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Sync now
          </button>
          <button onClick={() => sync(true)} disabled={busy !== null}
            className="inline-flex items-center gap-1 rounded-md border border-ek-purple/40 bg-white px-2.5 py-1 text-xs font-medium text-ek-purple hover:bg-ek-purple/5 disabled:opacity-60"
            data-testid={`connector-sync-full-${c.connector_id}`}
            title="Full re-sync — wipes synced plans and refetches everything">
            Full
          </button>
          {(c.last_sync_count || 0) > 0 && (
            <button onClick={onShowPlans}
              className="inline-flex items-center gap-1 rounded-md border border-black/10 bg-white px-2.5 py-1 text-xs font-medium text-ek-ink hover:bg-ek-purple/5"
              data-testid={`connector-plans-${c.connector_id}`}>
              <Database size={11} /> View plans
            </button>
          )}
          {def && (def.credential_fields || []).some((f) => SAFE_PATCH_KEYS.has(f.key)) && (
            <button onClick={() => setEditing(true)} disabled={busy !== null}
              className="inline-flex items-center gap-1 rounded-md border border-black/10 bg-white px-2.5 py-1 text-xs font-medium text-ek-ink hover:bg-ek-purple/5 disabled:opacity-60"
              data-testid={`connector-configure-${c.connector_id}`}
              title="Edit non-secret connector settings (e.g. ServiceNow table). Secret rotation still requires removing + re-adding the connector.">
              <Settings2 size={11} /> Configure
            </button>
          )}
          <button onClick={remove} disabled={busy !== null}
            className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-100 disabled:opacity-60"
            data-testid={`connector-delete-${c.connector_id}`}>
            <Trash2 size={11} />
          </button>
        </div>
      </div>
      {editing && (
        <ConfigureExistingDialog c={c} def={def}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); onChanged(); }}
        />
      )}
    </div>
  );
}

const SAFE_PATCH_KEYS = new Set(["table", "base_url"]);

function AutoSyncControl({ c, onChanged }) {
  // Local state is initialised from props on first render; thereafter our own
  // toggle/commit handlers update both local state AND the server, then call
  // onChanged() to reload the parent. Props can only diverge from local state
  // via an external mutation (another tab), which we accept as out-of-scope.
  const [enabled, setEnabled] = useState(Boolean(c.auto_sync_enabled));
  const [cadence, setCadence] = useState(c.auto_sync_cadence_min || 60);
  const [saving, setSaving] = useState(false);

  const patch = async (next) => {
    setSaving(true);
    try {
      await api.patch(`/connectors/${c.connector_id}`, next);
      toast.success(
        next.auto_sync_enabled === undefined
          ? "Auto-sync cadence updated"
          : next.auto_sync_enabled ? "Auto-sync enabled" : "Auto-sync paused"
      );
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
      // Revert local state on failure.
      setEnabled(Boolean(c.auto_sync_enabled));
      setCadence(c.auto_sync_cadence_min || 60);
    } finally {
      setSaving(false);
    }
  };

  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    patch({ auto_sync_enabled: next });
  };

  const commitCadence = () => {
    const cleaned = Math.max(15, Math.min(10080, parseInt(cadence, 10) || 60));
    setCadence(cleaned);
    if (cleaned !== (c.auto_sync_cadence_min || 60)) {
      patch({ auto_sync_cadence_min: cleaned });
    }
  };

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]"
         data-testid={`connector-autosync-${c.connector_id}`}>
      <button
        type="button"
        onClick={toggle}
        disabled={saving}
        data-testid={`connector-autosync-toggle-${c.connector_id}`}
        aria-pressed={enabled}
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-semibold transition-colors disabled:opacity-60 ${
          enabled
            ? "border-emerald-300 bg-emerald-50 text-emerald-800"
            : "border-black/10 bg-white text-ek-ink/60 hover:bg-ek-purple/5"
        }`}
      >
        <Zap size={10} className={enabled ? "fill-emerald-600 text-emerald-600" : ""} />
        Auto-sync {enabled ? "on" : "off"}
      </button>
      {enabled && (
        <label className="inline-flex items-center gap-1 text-ek-ink/65">
          every
          <input
            type="number"
            min={15}
            max={10080}
            step={15}
            value={cadence}
            onChange={(e) => setCadence(e.target.value)}
            onBlur={commitCadence}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitCadence(); } }}
            data-testid={`connector-autosync-cadence-${c.connector_id}`}
            className="w-16 rounded border border-black/10 px-1.5 py-0.5 text-center text-ek-ink"
          />
          minutes
        </label>
      )}
      {enabled && c.last_auto_sync_at && (
        <span className="text-ek-ink/45">
          · last auto-run {new Date(c.last_auto_sync_at).toLocaleString()}
        </span>
      )}
    </div>
  );
}


function ConfigureExistingDialog({ c, def, onClose, onSaved }) {
  // Edit only the non-secret credential fields (whitelisted server-side as
  // SAFE_CRED_KEYS={table, base_url}). For secret rotation, the user must
  // delete + re-add — surfaced as a footer note in the dialog.
  const editableFields = (def?.credential_fields || []).filter((f) => SAFE_PATCH_KEYS.has(f.key));
  const [values, setValues] = useState(() => {
    // Pre-fill with what the API exposes via `credential_keys` (just key names —
    // values are encrypted at rest). We can't show current values, so render
    // a placeholder pointing the user to the existing config.
    const initial = {};
    editableFields.forEach((f) => { initial[f.key] = ""; });
    return initial;
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    // Only send fields the user actually filled in. Empty string = leave as-is.
    const partial = {};
    Object.entries(values).forEach(([k, v]) => { if (v.trim()) partial[k] = v.trim(); });
    if (Object.keys(partial).length === 0) {
      toast.error("Nothing to update — fill in at least one field.");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/connectors/${c.connector_id}`, { credentials_partial: partial });
      toast.success(`${c.name} updated. Run Test + Sync to verify the new settings.`);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="configure-existing-dialog">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <h3 className="text-lg font-semibold text-ek-ink">Configure {c.name}</h3>
        <p className="mt-1 text-xs text-ek-ink/60">
          Edit non-secret settings (e.g. table, base URL). Secret rotation requires removing and re-adding the connector.
        </p>
        <div className="mt-4 space-y-3">
          {editableFields.length === 0 ? (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
              No editable fields for this connector type. Remove and re-add to change credentials.
            </p>
          ) : editableFields.map((f) => (
            <label key={f.key} className="block">
              <span className="text-xs font-medium text-ek-ink">{f.label}</span>
              <input
                type="text"
                value={values[f.key]}
                onChange={(e) => setValues((s) => ({ ...s, [f.key]: e.target.value }))}
                placeholder={`new value (current is encrypted at rest)`}
                data-testid={`configure-existing-${f.key}`}
                className="mt-1 w-full rounded-md border border-black/10 px-2 py-1.5 text-sm focus:border-ek-purple focus:outline-none"
              />
              {f.help && <span className="mt-0.5 block text-[10.5px] text-ek-ink/50">{f.help}</span>}
            </label>
          ))}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} disabled={busy}
            className="rounded-md border border-black/10 bg-white px-3 py-1.5 text-xs font-medium text-ek-ink hover:bg-ek-purple/5 disabled:opacity-60"
            data-testid="configure-existing-cancel">
            Cancel
          </button>
          <button onClick={save} disabled={busy || editableFields.length === 0}
            className="rounded-md bg-ek-purple px-3 py-1.5 text-xs font-semibold text-white hover:bg-ek-purple/90 disabled:opacity-60"
            data-testid="configure-existing-save">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}


function ConfigureDialog({ slug, catalogue, onClose, onCreated }) {  const def = catalogue.find((c) => c.slug === slug);
  const [creds, setCreds] = useState({});
  const [busy, setBusy] = useState(false);

  if (!def) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/connectors", { slug, credentials: creds });
      toast.success(`${def.name} connected — ${data.handshake?.detail || "ready"}`);
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not connect");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-6"
         onClick={onClose} data-testid="connector-configure-dialog">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-ek-ink">Configure {def.name}</h2>
          <button type="button" onClick={onClose}
            className="rounded-full p-1 text-ek-ink/65 hover:bg-ek-canvas hover:text-ek-ink"
            data-testid="connector-configure-close">✕</button>
        </div>
        <p className="mt-1 text-xs text-ek-ink/65">
          ERIO connects in read-only mode. Credentials are encrypted at rest.
        </p>
        <div className="mt-4 space-y-3">
          {def.credential_fields.map((f) => (
            <label key={f.key} className="block">
              <span className="text-xs font-medium text-ek-ink/60">{f.label}</span>
              <input
                type={f.type === "password" ? "password" : "text"}
                value={creds[f.key] || ""}
                onChange={(e) => setCreds((p) => ({ ...p, [f.key]: e.target.value }))}
                placeholder={f.help || ""}
                data-testid={`connector-field-${slug}-${f.key}`}
                className="mt-1 w-full rounded-md border border-black/10 px-3 py-2 text-sm"
              />
              {f.help && <p className="mt-0.5 text-[10.5px] text-ek-ink/55">{f.help}</p>}
            </label>
          ))}
        </div>
        <div className="mt-5 flex items-center gap-2">
          <button type="submit" disabled={busy}
            className="ek-btn flex items-center gap-1.5"
            data-testid={`connector-create-submit-${slug}`}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Plug size={14} />} Test &amp; connect
          </button>
          <button type="button" onClick={onClose}
            className="text-sm text-ek-ink/65 hover:text-ek-ink">Cancel</button>
        </div>
      </form>
    </div>
  );
}

function PlansDialog({ connectorId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get(`/connectors/${connectorId}/plans?limit=100`)
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData({ plans: [], count: 0 }); })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [connectorId]);

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/40"
         onClick={onClose} data-testid="connector-plans-dialog">
      <div className="relative h-full w-full max-w-2xl overflow-y-auto bg-white p-6 shadow-xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-ek-ink">
            {data?.connector?.name || "Connector"} · Synced plans
          </h2>
          <button onClick={onClose}
            className="rounded-full p-1 text-ek-ink/65 hover:bg-ek-canvas hover:text-ek-ink"
            data-testid="connector-plans-close">✕</button>
        </div>
        {loading ? (
          <p className="mt-4 text-sm text-ek-ink/65">Loading plans…</p>
        ) : data.plans.length === 0 ? (
          <p className="mt-4 text-sm text-ek-ink/65" data-testid="connector-plans-empty">No plans synced yet — click Sync now to fetch.</p>
        ) : (
          <table className="mt-4 w-full text-left text-xs">
            <thead className="text-[10.5px] uppercase tracking-wide text-ek-ink/55">
              <tr><th className="py-1.5 pr-2">Key</th><th className="py-1.5 pr-2">Title</th><th className="py-1.5 pr-2">Status</th><th className="py-1.5 pr-2">Due</th><th className="py-1.5">Source</th></tr>
            </thead>
            <tbody>
              {data.plans.map((p) => (
                <tr key={p.external_id} className="border-t border-black/5" data-testid={`plan-row-${p.external_id}`}>
                  <td className="py-1.5 pr-2 font-mono text-[11px]">{p.external_id}</td>
                  <td className="py-1.5 pr-2 text-ek-ink">{p.title}</td>
                  <td className="py-1.5 pr-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${
                      p.status === "complete" ? "bg-emerald-100 text-emerald-700"
                      : p.status === "blocked" ? "bg-red-100 text-red-700"
                      : p.status === "active" ? "bg-ek-purple/10 text-ek-purple"
                      : "bg-slate-100 text-slate-600"}`}>{p.status}</span>
                  </td>
                  <td className="py-1.5 pr-2 text-ek-ink/65">{p.due_date || "—"}</td>
                  <td className="py-1.5">
                    {p.source?.external_url && (
                      <a href={p.source.external_url} target="_blank" rel="noreferrer"
                         className="inline-flex items-center gap-1 text-ek-purple hover:underline">
                        <ExternalLink size={10} /> open
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

